import { useLazyQuery } from "@apollo/client";
import { compact, defer, first, last, uniqBy } from "lodash";
import moment from "moment";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useReducer,
  useRef,
} from "react";
import {
  GetMeetingOverviewMeetingsQueryQuery,
  GetMeetingOverviewMeetingsQueryQueryVariables,
  MeetingDialogFragmentFragment,
  MeetingsOverviewMeetingFragment,
} from "types/graphql-schema";

import { currentUserVar } from "@cache/cache";
import Loading from "@components/loading/loading";
import { classNames } from "@helpers/css";
import { assertEdgesNonNull } from "@helpers/helpers";

import getMeetingOverviewMeetingsQuery from "../graphql/get-meeting-overview-meetings-query";
import { MeetingType } from "./meetings-overview-constants";
import OverviewMeetingList from "./overview-meeting-list";

type MeetingItem =
  | MeetingsOverviewMeetingFragment
  | MeetingDialogFragmentFragment;

type DateRange = { start: string; end: string };

type State = {
  meetings: MeetingItem[];
  meetingDisplayedRange: DateRange;
  hasMoreInFuture: boolean;
  hasMoreInPast: boolean;
  loadMore: "past" | "future" | null;
  fillerHeight: number;
};

type Action =
  | { type: "RESET"; initialRange: DateRange; clearFillerHeight: boolean }
  | {
      type: "INITIAL_FETCH_COMPLETE";
      meetings: MeetingItem[];
      range?: DateRange;
      hasMoreInFuture: boolean;
    }
  | {
      type: "LOAD_MORE_COMPLETE";
      meetings: MeetingItem[];
      range?: DateRange;
      direction: "past" | "future";
      hasMore: boolean;
    }
  | { type: "SET_LOAD_MORE"; value: State["loadMore"] }
  | { type: "SET_FILLER_HEIGHT"; value: number };

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "RESET":
      return {
        ...state,
        meetings: [],
        meetingDisplayedRange: action.initialRange,
        hasMoreInFuture: false,
        hasMoreInPast: false,
        loadMore: null,
        fillerHeight: action.clearFillerHeight ? 0 : state.fillerHeight,
      };
    case "INITIAL_FETCH_COMPLETE":
      return {
        ...state,
        meetings: uniqBy([...state.meetings, ...action.meetings], "id"),
        hasMoreInPast: true,
        hasMoreInFuture: action.hasMoreInFuture,
        ...(action.range ? { meetingDisplayedRange: action.range } : undefined),
      };
    case "LOAD_MORE_COMPLETE": {
      const updates: Partial<State> = {
        meetings: uniqBy([...state.meetings, ...action.meetings], "id"),
      };
      if (action.direction === "future") {
        updates.hasMoreInFuture = action.hasMore;
      } else {
        updates.hasMoreInPast = action.hasMore;
      }
      if (action.range) {
        updates.meetingDisplayedRange = action.range;
      }
      return { ...state, ...updates };
    }
    case "SET_LOAD_MORE":
      return { ...state, loadMore: action.value };
    case "SET_FILLER_HEIGHT":
      return { ...state, fillerHeight: action.value };
    default:
      return state;
  }
};

const getInitialRange = (selectedDay: string, isCompact: boolean): DateRange =>
  isCompact
    ? {
        start: moment(selectedDay).startOf("day").format(),
        end: moment(selectedDay).endOf("day").format(),
      }
    : {
        start: moment(selectedDay).startOf("isoWeek").format(),
        end: moment(selectedDay).endOf("isoWeek").format(),
      };

const computeExpandedRange = (
  currentRange: DateRange,
  meetings: MeetingItem[],
): DateRange | undefined => {
  const firstMeeting = first(meetings);
  const lastMeeting = last(meetings);
  const sortedDates = compact([
    currentRange.start,
    currentRange.end,
    firstMeeting?.startDatetime &&
      moment(firstMeeting.startDatetime).startOf("day").format(),
    lastMeeting?.endDatetime &&
      moment(lastMeeting.endDatetime).endOf("day").format(),
  ]).sort();
  const firstDate = first(sortedDates);
  const lastDate = last(sortedDates);
  if (firstDate && lastDate) {
    return { start: firstDate, end: lastDate };
  }
  return undefined;
};

const MeetingOverview = ({
  selectedDay,
  meetingListClassName,
  onClickMeetingLink,
  forceTodayScroll,
  compact: isCompact = false,
  participantFilter = null,
  meetingTypeFilter = MeetingType.ALL,
}: {
  forceTodayScroll: number;
  selectedDay: string;
  meetingListClassName?: string;
  onClickMeetingLink?: (meetingId: number, meetingGroupId: number) => void;
  compact?: boolean;
  participantFilter?: number | null;
  meetingTypeFilter?: MeetingType;
}) => {
  const selectedUser = currentUserVar();
  const scrollTargetRef = useRef<HTMLDivElement>(null);
  const todayRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const fetchGenRef = useRef(0);
  const minimum = meetingTypeFilter === MeetingType.ONE_ON_ONE ? 5 : 10;

  const initialRange = getInitialRange(selectedDay, isCompact);

  const [state, dispatch] = useReducer(reducer, {
    meetings: [],
    meetingDisplayedRange: initialRange,
    hasMoreInFuture: false,
    hasMoreInPast: false,
    loadMore: null,
    fillerHeight: 0,
  });

  const [fetchMeetings, { loading }] = useLazyQuery<
    GetMeetingOverviewMeetingsQueryQuery,
    GetMeetingOverviewMeetingsQueryQueryVariables
  >(getMeetingOverviewMeetingsQuery);

  const updateFillerHeight = useCallback(() => {
    const targetEl = scrollTargetRef.current || todayRef.current;
    if (!targetEl || !scrollContainerRef.current) return;
    const container = scrollContainerRef.current;
    const fillerEl = container.querySelector("#filler");
    const currentFillerHeight = fillerEl ? fillerEl.clientHeight : 0;
    const contentHeight = container.scrollHeight - currentFillerHeight;
    const needed =
      container.clientHeight - (contentHeight - targetEl.offsetTop);
    dispatch({ type: "SET_FILLER_HEIGHT", value: Math.max(0, needed) });
  }, []);

  const scrollToRef = (
    ref: React.RefObject<HTMLDivElement | null>,
    instant = false,
  ) => {
    if (ref.current && scrollContainerRef.current) {
      const scrollTop = ref.current.offsetTop;
      scrollContainerRef.current.scrollTo({
        top: scrollTop - 10,
        behavior: instant ? "instant" : "smooth",
      });
    }
  };

  const fetchMoreMeetings = (
    dateRangeStart: string,
    dateRangeEnd: string,
    direction: "future" | "past",
  ) => {
    const gen = fetchGenRef.current;
    return fetchMeetings({
      variables: {
        participants: participantFilter
          ? [participantFilter]
          : [selectedUser.id],
        isOneonone:
          meetingTypeFilter === MeetingType.ALL
            ? undefined
            : meetingTypeFilter === MeetingType.ONE_ON_ONE,
        minimumDateRange: JSON.stringify({
          minimum,
          dateRange: [dateRangeStart, dateRangeEnd],
          direction,
        }),
        orderBy: "start_datetime",
        first: null,
      },
      onCompleted: (response) => {
        if (fetchGenRef.current !== gen) return;
        const newMeetings = response.meetings
          ? assertEdgesNonNull(response.meetings)
          : [];
        dispatch({
          type: "LOAD_MORE_COMPLETE",
          meetings: newMeetings,
          range: computeExpandedRange(state.meetingDisplayedRange, newMeetings),
          direction,
          hasMore: newMeetings.length >= minimum,
        });
      },
    });
  };

  useLayoutEffect(() => {
    if (!isCompact) {
      updateFillerHeight();
    }
  }, [state.meetings, state.meetingDisplayedRange, isCompact]);

  useEffect(() => {
    if (forceTodayScroll > 0) {
      scrollToRef(todayRef);
    }
  }, [forceTodayScroll]);

  useEffect(() => {
    if (state.loadMore === null) {
      return;
    }
    const dateRangeStart =
      state.loadMore === "past"
        ? moment(state.meetingDisplayedRange.start)
            .subtract(1, "week")
            .startOf("isoWeek")
            .format()
        : moment(state.meetingDisplayedRange.end)
            .add(1, "day")
            .startOf("day")
            .format();
    const dateRangeEnd =
      state.loadMore === "past"
        ? moment(state.meetingDisplayedRange.start)
            .subtract(1, "day")
            .endOf("day")
            .format()
        : moment(state.meetingDisplayedRange.end)
            .add(1, "week")
            .endOf("isoWeek")
            .format();
    fetchMoreMeetings(dateRangeStart, dateRangeEnd, state.loadMore).finally(
      () => {
        dispatch({ type: "SET_LOAD_MORE", value: null });
      },
    );
    // only want to fetch more when loadMore changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.loadMore]);

  const initialFetch = () => {
    const gen = fetchGenRef.current;
    const range = getInitialRange(selectedDay, isCompact);
    const selectedDayIsToday = moment().isSame(selectedDay, "day");
    const shouldFetchNextWeek =
      !isCompact && selectedDayIsToday && moment().isoWeekday() >= 4;
    fetchMeetings({
      variables: {
        participants: participantFilter
          ? [participantFilter]
          : [selectedUser.id],
        isOneonone:
          meetingTypeFilter === MeetingType.ALL
            ? undefined
            : meetingTypeFilter === MeetingType.ONE_ON_ONE,
        orderBy: "start_datetime",
        first: null,
        minimumDateRange: JSON.stringify({
          minimum: isCompact ? 0 : minimum,
          dateRange: [range.start, range.end],
          direction: "future",
        }),
      },
      onCompleted: (response) => {
        if (fetchGenRef.current !== gen) return;
        const newMeetings = response.meetings
          ? assertEdgesNonNull(response.meetings)
          : [];
        const expandedRange = !isCompact
          ? computeExpandedRange(range, newMeetings)
          : undefined;
        dispatch({
          type: "INITIAL_FETCH_COMPLETE",
          meetings: newMeetings,
          range: expandedRange,
          hasMoreInFuture: newMeetings.length >= minimum,
        });
        if (!isCompact) {
          defer(() => {
            scrollToRef(scrollTargetRef, true);
          });
        }
        // On Thursday/Friday, fetch next week if the expanded range
        // didn't already extend beyond the current week
        if (
          shouldFetchNextWeek &&
          expandedRange &&
          moment(expandedRange.end).isSameOrBefore(range.end, "day")
        ) {
          dispatch({ type: "SET_LOAD_MORE", value: "future" });
        }
      },
    });
  };

  useEffect(() => {
    // Increment generation to invalidate any in-flight fetches
    fetchGenRef.current += 1;
    // reset meetings and pagination states
    dispatch({
      type: "RESET",
      initialRange,
      clearFillerHeight: isCompact,
    });
    // defer fetch so the state is updated, when function is called.
    defer(() => {
      initialFetch();
    });
  }, [isCompact, selectedDay, participantFilter, meetingTypeFilter]);

  const handleSetLoadMore = useCallback((value: "past" | "future" | null) => {
    dispatch({ type: "SET_LOAD_MORE", value });
  }, []);

  const meetingsToDisplay = isCompact
    ? state.meetings.filter((m) =>
        moment(m.startDatetime).isSame(moment(selectedDay), "day"),
      )
    : state.meetings;

  return (
    <div
      className={classNames(
        "@container/overview-meetings",
        isCompact ? "relative" : "h-full",
      )}
    >
      <div
        className={classNames(
          isCompact
            ? "px-3 pt-1 overflow-y-auto scrollbar-hidden"
            : "absolute inset-0 overflow-y-scroll scrollbar-hidden pb-12 px-3 pt-4",
          meetingListClassName,
        )}
        ref={scrollContainerRef}
      >
        {/* Main loader on first page hit */}
        {loading && meetingsToDisplay.length === 0 ? (
          <Loading className="p-6" size={6}>
            Loading meetings...
          </Loading>
        ) : meetingsToDisplay.length === 0 ? (
          <div className="text-gray-500 px-2">No meetings.</div>
        ) : (
          <OverviewMeetingList
            ref={scrollTargetRef}
            meetings={meetingsToDisplay}
            loading={loading}
            participantFilter={participantFilter}
            meetingTypeFilter={meetingTypeFilter}
            meetingDisplayedRange={
              isCompact
                ? {
                    start: moment(selectedDay).startOf("day").format(),
                    end: moment(selectedDay).endOf("day").format(),
                  }
                : state.meetingDisplayedRange
            }
            onClickMeetingLink={onClickMeetingLink}
            canLoadMoreInFuture={state.hasMoreInFuture}
            canLoadMoreInPast={state.hasMoreInPast}
            loadMore={state.loadMore}
            onChangeLoadMore={handleSetLoadMore}
            isShowingWeekends={true}
            fillerHeight={state.fillerHeight}
            compact={isCompact}
            scrollToDay={selectedDay}
            todayRef={todayRef}
          />
        )}
      </div>
    </div>
  );
};

export default React.memo(MeetingOverview);
